import { useEffect } from 'react'
import { LuFolder } from 'react-icons/lu'
import type { LinkProps } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import List from '@mui/material/List'
import Box from '@mui/material/Box'
import useFiles from '../models/useFiles'
import { targetKey, type FileTree } from '../lib/webdavClient'
import { formatBytes } from '../utilities/general'
import ListItemLink from './ListItemLink'

// The Storage section, shared by the node and network pages.
//
// A network's share and a node's own area are the same thing addressed
// differently, so they get the same section: one heading, one row, one icon,
// one word. They used to be two copies that had already drifted — the node
// said "This node's storage" under a "Files" heading, the network repeated its
// own name — which is the drift a single component makes impossible rather
// than merely discouraged. `FileBrowser` behind the link is shared for the
// same reason.
//
// The size is the CURRENT total, walked from the tree itself (useFiles.loadSize)
// rather than read off a meter: the network's metered figure is a period peak
// meant for billing, and node storage has no meter at all. One number that
// means one thing on both pages beats two numbers that don't.
export default function StorageSection({
  tree,
  id,
  linkState,
}: {
  tree: FileTree
  id: string
  linkState?: LinkProps['state'] // where the browser's Back should return to
}) {
  const loadSize = useFiles(state => state.loadSize)
  const bytes = useFiles(state => state.sizes[targetKey({ tree, id })])

  useEffect(() => {
    void loadSize({ tree, id })
  }, [tree, id, loadSize])

  return (
    <Box>
      <Typography variant="h6">Storage</Typography>
      <List disablePadding>
        {/* The route mirrors the WebDAV tree — `/nodes/<id>/files`,
            `/networks/<id>/files` — so one template covers both. */}
        <ListItemLink to={`/${tree}/${id}/files`} icon={<LuFolder />} state={linkState}>
          Files
          {bytes !== undefined && (
            <Typography component="span" color="textDisabled" sx={{ marginLeft: 1 }}>
              {formatBytes(bytes)}
            </Typography>
          )}
        </ListItemLink>
      </List>
    </Box>
  )
}
